import { glifoDoMotivo, type MotivoLinha } from '@miolos/core';

interface Enfeite {
  motivo: MotivoLinha;
  /** Posição em porcentagem da caixa que envolve a decoração. */
  x: number;
  y: number;
  /** Tamanho em pixels. */
  tamanho: number;
  giro: number;
  cor: string;
  /** Atraso da animação, para os desenhos não subirem todos juntos. */
  atraso: number;
  opacidade: number;
}

/*
 * Os desenhos saem do mesmo catálogo que enfeita a pauta impressa (corações,
 * sol, folha, laço, lua...). Reusar os glifos é de propósito: o site passa a
 * falar a mesma língua visual do que a pessoa vai ver no papel.
 *
 * As posições evitam o centro — é ali que fica o texto — e as cores vêm dos
 * tons alegres declarados em `index.css`.
 */
const ENFEITES: Enfeite[] = [
  { motivo: 'SOL', x: 4, y: 8, tamanho: 36, giro: -12, cor: 'var(--cor-alegre-2)', atraso: 0, opacidade: 0.9 },
  { motivo: 'CORACAO', x: 11, y: 62, tamanho: 24, giro: 10, cor: 'var(--cor-alegre-1)', atraso: 1.1, opacidade: 0.85 },
  { motivo: 'ESTRELA', x: 7, y: 33, tamanho: 20, giro: 16, cor: 'var(--cor-alegre-4)', atraso: 2.3, opacidade: 0.8 },
  { motivo: 'FOLHA', x: 92, y: 14, tamanho: 32, giro: 14, cor: 'var(--cor-alegre-5)', atraso: 0.6, opacidade: 0.85 },
  { motivo: 'LACO', x: 87, y: 70, tamanho: 28, giro: -10, cor: 'var(--cor-alegre-1)', atraso: 1.8, opacidade: 0.85 },
  { motivo: 'XICARA', x: 95, y: 44, tamanho: 24, giro: 8, cor: 'var(--cor-alegre-3)', atraso: 2.8, opacidade: 0.8 },
  { motivo: 'LUA', x: 16, y: 88, tamanho: 22, giro: -14, cor: 'var(--cor-alegre-4)', atraso: 3.4, opacidade: 0.75 },
  { motivo: 'FLOR', x: 82, y: 92, tamanho: 26, giro: 12, cor: 'var(--cor-alegre-2)', atraso: 0.9, opacidade: 0.8 },
  { motivo: 'LAPIS', x: 30, y: 3, tamanho: 22, giro: -8, cor: 'var(--cor-alegre-3)', atraso: 2.1, opacidade: 0.7 },
  { motivo: 'GOTA', x: 68, y: 96, tamanho: 20, giro: 10, cor: 'var(--cor-alegre-3)', atraso: 1.4, opacidade: 0.7 },
];

/**
 * Enfeites soltos em volta de um bloco, com um balanço bem discreto.
 *
 * É decoração de tela, e só de tela: `aria-hidden`, `pointer-events: none` e
 * escondida no celular, onde o espaço é do texto. O balanço existe apenas para
 * quem **não** pediu menos movimento no sistema (`prefers-reduced-motion`) — sem
 * ele, os desenhos ficam parados, no lugar.
 */
export function MotivosFlutuantes({ className }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={['pointer-events-none absolute inset-0 hidden select-none md:block', className]
        .filter(Boolean)
        .join(' ')}
    >
      {ENFEITES.map((enfeite) => {
        const glifo = glifoDoMotivo(enfeite.motivo);
        if (!glifo) return null;

        return (
          <span
            key={`${enfeite.motivo}-${enfeite.x}-${enfeite.y}`}
            className="absolute"
            style={{
              left: `${enfeite.x}%`,
              top: `${enfeite.y}%`,
              rotate: `${enfeite.giro}deg`,
              opacity: enfeite.opacidade,
            }}
          >
            <span
              className="flutua block"
              style={{ animationDelay: `${enfeite.atraso}s` }}
              // O glifo é um desenho de contorno, do mesmo catálogo da pauta.
              dangerouslySetInnerHTML={{
                __html: `<svg viewBox="0 0 24 24" width="${enfeite.tamanho}" height="${enfeite.tamanho}" fill="none" stroke="${enfeite.cor}" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">${glifo.replace(
                  /__COR__/g,
                  enfeite.cor,
                )}</svg>`,
              }}
            />
          </span>
        );
      })}
    </div>
  );
}
